/**
 * Shared HTML builders for the "Download → HTML" export. Produces a
 * self-contained document (inline CSS) that mirrors the on-screen cards.
 */

import type { EvidenceTag } from './evidence';

export const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  );

export const tagSpanHtml = (tag: EvidenceTag | null): string =>
  tag ? `<span class="tag tag-${tag.toLowerCase()}">${esc(tag)}</span>` : '';

/** Shared stylesheet for all artifact HTML exports. */
const STYLES = `
 *{box-sizing:border-box}body{margin:0;padding:32px 16px;background:#f8fafc;color:#0f172a;
 font-family:"IBM Plex Sans Arabic",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;line-height:1.55}
 .wrap{max-width:720px;margin:0 auto;display:flex;flex-direction:column;gap:16px}
 .card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:20px 24px}
 .card h3{margin:0 0 8px;font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:#6b7280}
 .score-top{display:flex;align-items:baseline;gap:12px}
 .score-pct{font-size:30px;font-weight:700}.score-pct.amber{color:#f59e0b}.score-pct.emerald{color:#10b981}
 .score-label{font-size:14px;font-weight:600;color:#374151}
 .bar{margin-top:12px;height:10px;width:100%;background:#e5e7eb;border-radius:999px;overflow:hidden}
 .bar-fill{height:100%;border-radius:999px}.bar-fill.amber{background:#fbbf24}.bar-fill.emerald{background:#34d399}
 .summary{margin:8px 0 0;font-size:12px;color:#6b7280}
 .row{display:grid;grid-template-columns:160px 1fr;gap:16px;padding:12px 0;border-top:1px solid #f3f4f6}
 .row:first-of-type{border-top:0}.label{font-size:14px;font-weight:600;color:#111827}.label.hl{color:#ea580c}
 .value p{margin:0;font-size:14px;color:#1f2937}.value ul{margin:0;padding-left:18px}.value li{font-size:14px;color:#1f2937;margin:2px 0}
 .tag{display:inline-block;margin-left:4px;padding:1px 8px;border-radius:999px;font-size:10px;font-weight:600;vertical-align:middle}
 .tag-grounded{background:#ecfdf5;color:#047857}.tag-assumed{background:#fffbeb;color:#b45309}.tag-inferred{background:#eff6ff;color:#1d4ed8}
 .chips{display:flex;flex-wrap:wrap;gap:8px}.chip{display:inline-block;padding:4px 10px;border:1px solid #fcd34d;background:#fffbeb;color:#78350f;border-radius:6px;font-size:12px;font-weight:500}
 ol{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:16px}
 .step-label{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:#9ca3af}
 .q{font-size:14px;font-weight:600;color:#111827}.a{margin:2px 0 0;font-size:14px;color:#374151}
 .card.accent{border-color:#a7f3d0;background:#ecfdf580}.card.accent h3{color:#047857}.card.accent p{margin:0;font-size:14px;color:#1f2937}
 @media(max-width:520px){.row{grid-template-columns:1fr;gap:6px}}
`;

export const htmlDoc = (title: string, body: string): string =>
  `<!doctype html>
<html lang="en" dir="ltr"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/><title>${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&display=swap" rel="stylesheet"/>
<style>${STYLES}</style></head><body><div class="wrap">${body}</div></body></html>`;

/** Score header block (matches ScoreHeader). */
export const scoreHtml = (
  score: number,
  label: string,
  tone: 'amber' | 'emerald',
  summary?: string | null
): string => {
  const clamped = Math.max(0, Math.min(100, score));
  return `<section class="card"><div class="score-top"><span class="score-pct ${tone}">${clamped}%</span>
    <span class="score-label">${esc(label)}</span></div>
    <div class="bar"><div class="bar-fill ${tone}" style="width:${clamped}%"></div></div>
    ${summary ? `<p class="summary">${esc(summary)}</p>` : ''}</section>`;
};
